/**
 * Mobile video source rewriting — makes "one video for PC / another video for mobile" a site-wide capability.
 *
 * ## Why "rewrite" rather than "pick the source at each load point"
 *
 * This repo has four unrelated video-loading implementations:
 *   1. soulog-video-load-policy.js  — raises preload + load() (sticky-steps / feature-reveal
 *                                      / expertise-scroll / showcase-scroll)
 *   2. soulog-curtain-reveal.js     — moves data-src into src
 *   3. scroll-reel.js               — warms up once scrolling settles
 *   4. scroll-video-split.js        — progressiveAttach(low → hi) two-tier progressive loading
 *
 * Writing "pick the source by viewport" separately at each consumer would mean changing four places, and every future video section would have to
 * remember to hook in too (missing one raises no error, it just silently keeps downloading the large desktop file on phones — the hardest kind of bug to notice).
 * The rewrite happens **before all consumers**: they keep reading src / data-src as before, and what they read is already the correct one;
 * not a single line of the four loading implementations needs to change, and a future fifth one works automatically.
 *
 * ## Execution timing (this is the only timing constraint in this file; read before changing anything)
 *
 * This script is included with defer before </head> in layout/theme.liquid, therefore:
 *   · defer guarantees it runs after the DOM is parsed — every <video> on the page already exists, so none are missed;
 *   · being in <head> means it comes before all defer scripts inside sections in document order,
 *     and defer scripts run in document order → it always finishes before the four loading implementations above.
 * Both are required. **Don't move it to the bottom of body** (it would run after the section scripts, changing the source after the video has already started downloading,
 * wasting a whole download), and **don't remove defer** (the DOM wouldn't be parsed yet and not a single video would be found).
 *
 * ⚠️ This used to say "at this moment all target videos are preload="none" and haven't been load()ed, so changing the source won't interrupt
 * an in-progress download" — **that premise is wrong**: the slideshow's first image and the homepage hero are both autoplay +
 * preload="auto", starting to download as soon as they're parsed, so on real pages resource selection has often already completed, and changing only the <source>
 * attribute is a no-op. For the fix see the comment on reselect() below (call load() once after the change).
 *
 * ## Two marker forms (why they aren't unified into one)
 *
 *   1. Hand-written <video> → data-src-mobile="<url>" directly on the element
 *   2. video_tag filter output → a sibling <template data-src-mobile="<url>">
 *
 * The second exists because **Liquid filter parameter names can't contain hyphens**; `data-src-mobile:` is a syntax error,
 * so there's no way to pass the URL into video_tag (the same restriction forced video-load-policy to pass its marker via class,
 * see the comment in snippets/soulog-feature-reveal-media.liquid). <template> was chosen over
 * <span hidden> because a template's content isn't rendered, doesn't take part in layout, and isn't matched by CSS,
 * so inserting it into any container won't disturb the existing layout — the card layouts of those four sections are sensitive to an extra child node.
 *
 * ## Breakpoint
 *
 * 767px = the repo's main breakpoint (see the responsive guidelines in CLAUDE.md), same source as soulog-video-load-policy.js,
 * and is the **default**. An individual element can carry data-src-mobile-mq="<media query>" to override it with another breakpoint
 * (the homepage Soulog Sense uses it to keep the portrait footage all the way up to 1023px) — the check is done per element, see handle.
 * Elements that don't match never enter the rewrite logic — sections without a mobile video behave byte-for-byte the same as before this change.
 *
 * ## Re-evaluate after viewport changes (added 2026-09-17)
 *
 * Source swapping is **bidirectional**, with a change listener on the media query: when the viewport crosses the breakpoint, apply runs again,
 * swapping to the mobile source when narrower and back to the original desktop source when wider. Previously it was only checked once at load, so every path that "changes the viewport without reloading the page"
 * (switching device preview in the editor / dragging the window / rotating a tablet) left the CSS and footage mismatched — see the comment on mqlFor for the symptom.
 * Swapping back relies on the restore function returned by swapTo (stored in the restorers WeakMap), so every new rewrite branch
 * must be written together with its matching restore.
 *
 * ## Ultra-wide tier (data-src-wide, added 2026-09-18)
 *
 * The same mechanism in the opposite direction: <video data-src-wide="<url>"> at ≥2000px (overridable with data-src-wide-mq)
 * swaps to an ultra-wide source. Currently only the homepage Soulog Sense uses it — above 1920 its height is capped at 1080, so 16:9 footage on
 * wider screens gets its top and bottom cropped by cover, hence a separate version for large screens. Only the "attribute on the video" form is supported;
 * the <template> form wasn't built since there's no need for it. When a video carries both tiers, mobile takes priority (the two ranges don't overlap anyway).
 * State is recorded by "which tier is current", not by URL: re-running apply on the same tier doesn't rewrite twice (idempotent),
 * and when switching tiers it first restores to the desktop state, then swaps — the restore function only knows the original desktop DOM, so it can't switch directly between tiers.
 *
 * ## Backward compatibility
 *
 * Merchant didn't fill in a mobile video → Liquid outputs no marker → nothing is found here → the desktop video is used as before.
 * So this capability has zero impact on existing templates/*.json, and nothing needs to be configured in the theme editor first.
 */
(function () {
	"use strict";

	// Same as video-load-policy: runs only once when multiple sections on the same page each include it.
	// (This file is currently included globally by theme.liquid, so there's only one copy; this flag is a
	//  fallback in case someone later changes it to per-section inclusion.)
	if (window.__soulogMobileVideoSrc) return;
	window.__soulogMobileVideoSrc = true;

	var MOBILE_MQ = "(max-width: 767px)";
	var ATTR = "data-src-mobile";
	var MQ_ATTR = "data-src-mobile-mq";
	var WIDE_MQ = "(min-width: 2000px)";
	var WIDE_ATTR = "data-src-wide";
	var WIDE_MQ_ATTR = "data-src-wide-mq";

	/**
	 * video → { variant: 'mobile' | 'wide', restore: a function that "restores it to the desktop state" }.
	 * It's keyed here rather than in a data-* attribute because more than a boolean needs to be stored: it also includes the removed
	 * <source> nodes themselves (see swapTo). An entry = a replacement source is currently attached; none = desktop state.
	 * When swapping back, take out restore, run it, then delete the entry.
	 */
	var restorers = new WeakMap();

	/** Media queries that already have a change listener attached, for deduplication (most pages have only the 767 one). */
	var watched = {};

	/**
	 * Get a media query's MediaQueryList, and **attach cross-boundary re-evaluation along the way**.
	 *
	 * ⚠️ This listener is required, not a nice-to-have. If checked only once at load, every path that "changes the viewport without reloading the page"
	 * leaves the CSS (which follows media queries live) and the footage (stuck with the decision made at load time) mismatched:
	 *   · switching device preview in the theme editor — it only changes the preview frame width, without reloading the iframe
	 *   · manually dragging the browser window, the DevTools device toolbar
	 *   · rotating a tablet (iPad portrait 768 / landscape 1024, exactly straddling the homepage Sense's 1023 line)
	 * The symptom was the homepage Soulog Sense on tablets stuffing 16:9 desktop footage into a 3:4 portrait frame,
	 * where cover scales it up by height and crops nearly 30% off each side (only 42% of the width remains visible).
	 *
	 * change only fires **at the moment of crossing the boundary**; the intermediate steps of dragging a window don't swap sources continuously,
	 * so no extra debouncing is needed here.
	 */
	function mqlFor(query) {
		var mql = window.matchMedia(query);
		if (watched[query] || !mql) return mql;
		watched[query] = true;
		var onChange = function () {
			apply(document);
		};
		if (typeof mql.addEventListener === "function") mql.addEventListener("change", onChange);
		// Safari < 14 only has the deprecated addListener, and devices like that still visit this store
		else if (typeof mql.addListener === "function") mql.addListener(onChange);
		return mql;
	}

	/**
	 * Swap to the mobile source, and return a function that restores the DOM exactly to its desktop state.
	 *
	 * All three markup forms must be covered (all three are in use in this repo):
	 *   a. <video data-src="...">              lazy-load type (curtain-reveal / SVS / scroll-reel external-URL branch)
	 *   b. <video><source src="..."></video>   real-src type (sticky-steps / video_tag output / various url fallbacks)
	 *   c. <video src="...">                   src directly on the element (currently unused, covered for completeness)
	 * The check order must not be reversed: a form-b video may also have data-src (SVS does), so checking data-src first is correct.
	 *
	 * Each branch must write the forward rewrite and the restore **as a pair** — missing one attribute on the way back shows up as "after dragging from tablet back to desktop
	 * it keeps using the 600px-wide portrait footage", which looks visibly blurry on large screens, and raises no error at all.
	 */
	function swapTo(video, url) {
		// a. Lazy-load: change data-src and let the original loading logic move it. There's no real src yet at this point,
		//    so load() isn't needed and shouldn't be called (it would only make the browser run resource selection for nothing).
		if (video.dataset.src) {
			var oldSrc = video.dataset.src;
			// SVS's progressive two tiers: once the main source is swapped to the mobile version, the low-res transition tier must be invalidated too —
			// it's a 1/8 transition version of the desktop high-res; keeping it would make the phone download both low and mobile,
			// using more data than not having this feature at all. When swapping back it must be put back as it was.
			var oldLow = video.dataset.srcLow;
			video.dataset.src = url;
			if (oldLow) delete video.dataset.srcLow;
			return function () {
				video.dataset.src = oldSrc;
				if (oldLow) video.dataset.srcLow = oldLow;
			};
		}

		// b. <source> children: Shopify-hosted videos output several (one per bitrate tier);
		//    mobile only wants one explicit source, so replace them all rather than changing the first —
		//    if the other tiers remain, the browser may still pick the desktop one. The removed nodes must be kept, to be put back as they were when swapping back.
		var sources = video.querySelectorAll("source");
		if (sources.length) {
			var extras = [];
			for (var i = 1; i < sources.length; i++) extras.push(sources[i]);
			for (var k = extras.length - 1; k >= 0; k--) extras[k].parentNode.removeChild(extras[k]);

			var first = sources[0];
			// Lazy-load-type <source data-src> (curtain-reveal's Shopify video branch)
			// and the real-src type must be handled separately; changing the wrong one is the same as not changing it.
			var lazy = first.hasAttribute("data-src");
			var attr = lazy ? "data-src" : "src";
			var oldVal = first.getAttribute(attr);
			var oldType = first.getAttribute("type");
			first.setAttribute(attr, url);
			first.setAttribute("type", "video/mp4");
			if (!lazy) reselect(video);

			return function () {
				first.setAttribute(attr, oldVal);
				if (oldType === null) first.removeAttribute("type");
				else first.setAttribute("type", oldType);
				// Restore order: first never moved, the rest are appended after it in their original order.
				// These videos' children are only <source>, so appending to the end preserves the original order.
				for (var j = 0; j < extras.length; j++) video.appendChild(extras[j]);
				if (!lazy) reselect(video);
			};
		}

		// c. src directly on the video
		var oldOwn = video.getAttribute("src");
		video.setAttribute("src", url);
		reselect(video);
		return function () {
			if (oldOwn === null) video.removeAttribute("src");
			else video.setAttribute("src", oldOwn);
			reselect(video);
		};
	}

	/**
	 * Swap to a given replacement-source tier (do nothing if already on that tier — section:load re-runs apply).
	 * When coming from the other tier, first restore to the desktop state: the restore function only knows the original desktop DOM.
	 */
	function toVariant(video, variant, url) {
		if (!video || !url) return;
		var cur = restorers.get(video);
		if (cur && cur.variant === variant) return;
		if (cur) toDesktop(video);
		restorers.set(video, { variant: variant, restore: swapTo(video, url) });
	}

	/** Back to the desktop source (does nothing if never swapped — sections without a replacement video are never touched at all). */
	function toDesktop(video) {
		if (!video) return;
		var cur = restorers.get(video);
		if (!cur) return;
		restorers.delete(video);
		cur.restore();
	}

	/**
	 * After changing the real src, the browser must be made to re-run the "resource selection algorithm".
	 *
	 * HTML spec: changing a <source> element's attributes after it has been inserted into a media element is a **no-op** —
	 * the browser has long since chosen and started downloading that one; without load() it stays that way.
	 *
	 * Measured 2026-08-28 (Chrome, using an 800~1200ms busy-wait to simulate a real heavy page with "lots of markup
	 * and scripts after the video"):
	 *   · before the swap currentSrc=pc.mp4, networkState=2(LOADING) → without load() it ends up as pc.mp4
	 *     (the swap silently fails, and phones still download the desktop version); with load() it ends up as mobile.mp4.
	 *   · on a very light page DOMContentLoaded **happens** to run before resource selection (networkState=3
	 *     NO_SOURCE, currentSrc empty), in which case it works even without load.
	 * So this is a race: the heavier the page and the slower the device, the more likely it loses. The homepage hero is precisely the worst case
	 * (autoplay + preload="auto", followed by six feature cards and several scripts).
	 *
	 * Why it has no side effects: this function is only reached when **a source swap has already been confirmed** (sections without a mobile video
	 * never enter swapTo at all), and during the load-time pass no video is playing; for lazy-loaded videos with preload="none",
	 * calling load() only re-runs selection and doesn't trigger a download. The data-src form returns above and never gets here —
	 * that path hasn't reached the "has a src" stage yet, so load() would be redundant.
	 */
	function reselect(video) {
		if (typeof video.load !== "function") return;
		// Videos that "play once and freeze" (data-play-once, see soulog-bg-video.js) get special handling:
		// once it plays to ended its observer is removed, and it will never play again. But load() would knock it back
		// to the unloaded state — after a cross-breakpoint source swap the picture would collapse to blank/the first frame, instead of the designed frozen last frame.
		// So seek back to the last frame ourselves here, matching the look before the swap. During the load-time pass playedOnce hasn't been set yet,
		// so this branch isn't reached.
		var wasFrozen = !!video.dataset.playedOnce;
		video.load();
		if (!wasFrozen || typeof video.addEventListener !== "function") return;
		var toEnd = function () {
			video.removeEventListener("loadedmetadata", toEnd);
			var d = video.duration;
			if (typeof d !== "number" || !isFinite(d) || d <= 0) return;
			// Don't use duration directly: some browsers treat that as "playback ended" and fire ended again.
			try {
				video.currentTime = Math.max(0, d - 0.05);
			} catch (e) {
				/* A few browsers throw when seeking before metadata is ready; failing to freeze shouldn't drag down the source swap */
			}
		};
		video.addEventListener("loadedmetadata", toEnd);
	}

	/**
	 * Find the video corresponding to a <template data-src-mobile>.
	 * Convention: the template and video share a parent (output right next to each other in Liquid). Check the following siblings first,
	 * then fall back to searching within the same parent — some sections insert comment nodes or poster elements between the two.
	 */
	function videoFor(tpl) {
		var next = tpl.nextElementSibling;
		while (next) {
			if (next.tagName === "VIDEO") return next;
			// Only skip obvious non-media nodes, and don't search indefinitely — otherwise it might latch onto the next card's video.
			if (next.tagName !== "TEMPLATE" && next.querySelector) {
				var inner = next.querySelector("video");
				if (inner) return inner;
			}
			next = next.nextElementSibling;
		}
		return tpl.parentNode ? tpl.parentNode.querySelector("video") : null;
	}

	function apply(root) {
		if (!window.matchMedia) return;
		var scope = root && root.querySelectorAll ? root : document;

		// Form 1: attributes on the video element itself (mobile tier / ultra-wide tier, either one or both)
		var direct = scope.querySelectorAll("video[" + ATTR + "]");
		for (var i = 0; i < direct.length; i++) handle(direct[i], direct[i], direct[i].getAttribute(ATTR));
		var wide = scope.querySelectorAll("video[" + WIDE_ATTR + "]");
		for (var w = 0; w < wide.length; w++) {
			// Those with both were already checked together above (handle reads the wide attribute); here we only add those with only wide
			if (!wide[w].hasAttribute(ATTR)) handle(wide[w], wide[w], null);
		}

		// Form 2: carried by a sibling template
		var tpls = scope.querySelectorAll("template[" + ATTR + "]");
		for (var j = 0; j < tpls.length; j++) handle(tpls[j], videoFor(tpls[j]), tpls[j].getAttribute(ATTR));
	}

	/**
	 * Decide which way a marker should go.
	 *
	 * The breakpoint check is done **on each element** individually, not as a single cut at the start of apply — an element carrying a marker can use
	 * data-src-mobile-mq to override its own breakpoint (the homepage Soulog Sense uses it to keep the portrait footage all the way up to 1023px).
	 * ⚠️ To make a section swap sources on tablets too, **only use this attribute; don't change MOBILE_MQ** —
	 * this file is included globally by layout/theme.liquid and shared by 12 sections; changing the constant would swap every video site-wide
	 * to mobile footage on tablets (that footage is produced at ~400px wide and would look blurry stretched to 1024).
	 *
	 * Both directions must be checked: if only "entering mobile" were checked, dragging from tablet back to desktop would keep using the small portrait footage.
	 */
	function handle(marker, video, url) {
		if (url) {
			var mql = mqlFor(marker.getAttribute(MQ_ATTR) || MOBILE_MQ);
			if (mql && mql.matches) return toVariant(video, "mobile", url);
		}
		// The ultra-wide tier only recognizes attributes on the video itself (the template form isn't supported, see the file header)
		var wideUrl = marker === video && video ? video.getAttribute(WIDE_ATTR) : null;
		if (wideUrl) {
			var wmql = mqlFor(video.getAttribute(WIDE_MQ_ATTR) || WIDE_MQ);
			if (wmql && wmql.matches) return toVariant(video, "wide", wideUrl);
		}
		toDesktop(video);
	}

	apply(document);

	// Adding/removing sections in the theme editor re-mounts the DOM; newly arrived videos likewise need their source fixed before loading.
	document.addEventListener("shopify:section:load", function (e) {
		apply(e.target || document);
	});

	// For use by tests and by future callers that insert content dynamically.
	window.soulogApplyMobileVideoSrc = apply;
})();
